import { PersonLane } from '@app/modules/persons/appearances/types';
import { TimeRange } from '@mi-front/core';
import { MrtTable } from '@ui/components';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { personsViewMessages, SourceFilter } from './persons-view-messages';
import { PersonsViewAppearanceDetail } from './PersonsViewAppearanceDetail';
import { PersonsViewDurationCell } from './PersonsViewDurationCell';
import { PersonsViewFilters } from './PersonsViewFilters';
import { PersonsViewPersonCell } from './PersonsViewPersonCell';
import { PersonsViewSourceChips } from './PersonsViewSourceChips';

function matchesFilter(lane: PersonLane, filter: SourceFilter): boolean {
  switch (filter) {
    case 'face':
      return lane.face.length > 0;
    case 'voice':
      return lane.voice.length > 0;
    case 'unknown':
      return lane.isUnknown;
    default:
      return true;
  }
}

type PersonsViewProps = {
  /** Lanes already clipped to the focus range (see usePersonsTrackLanes). */
  lanes: PersonLane[];
  loading: boolean;
  /** Focus range: scales the mini timeline in the detail panel. */
  range: TimeRange<number>;
};

/**
 * Personas results-tab table for the current focus window.
 *
 * Shows one row per person (identity, sources, on-screen / speaking totals,
 * appearance count) with expandable detail of every detection. Filter chips
 * above the table narrow by face, voice, or unknown. The table stays mounted
 * when a filter yields no rows so headers and tools remain visible.
 */
export function PersonsView({ lanes, loading, range }: PersonsViewProps) {
  const { formatMessage } = useIntl();
  const [filter, setFilter] = useState<SourceFilter>('all');

  const data = useMemo(
    () =>
      lanes
        .filter((l) => matchesFilter(l, filter))
        .sort((a, b) => b.faceTotalMs + b.voiceTotalMs - (a.faceTotalMs + a.voiceTotalMs)),
    [lanes, filter],
  );

  return (
    <>
      <PersonsViewFilters filter={filter} onFilterChange={setFilter} />
      <MrtTable<PersonLane>
        columns={[
          {
            id: 'person',
            header: formatMessage(personsViewMessages.title),
            accessorFn: (row) => row.laneKey,
            Cell: ({ row }) => (
              <PersonsViewPersonCell
                lane={row.original}
                expanded={row.getIsExpanded()}
                onToggleExpand={() => row.toggleExpanded()}
              />
            ),
            enableSorting: false,
            size: 260,
          },
          {
            id: 'sources',
            header: formatMessage(personsViewMessages.sources),
            Cell: ({ row: { original } }) => <PersonsViewSourceChips lane={original} />,
            enableSorting: false,
            size: 120,
          },
          {
            id: 'onScreen',
            header: formatMessage(personsViewMessages.onScreen),
            accessorFn: (row) => row.faceTotalMs,
            Cell: ({ row: { original } }) => <PersonsViewDurationCell ms={original.faceTotalMs} />,
            size: 90,
          },
          {
            id: 'speaking',
            header: formatMessage(personsViewMessages.speaking),
            accessorFn: (row) => row.voiceTotalMs,
            Cell: ({ row: { original } }) => <PersonsViewDurationCell ms={original.voiceTotalMs} />,
            size: 90,
          },
          {
            id: 'appearances',
            header: formatMessage(personsViewMessages.appearances),
            accessorFn: (row) => row.appearances,
            size: 80,
          },
        ]}
        data={data}
        renderDetailPanel={({ row }) => (
          <PersonsViewAppearanceDetail lane={row.original} range={range} />
        )}
        muiDetailPanelProps={{
          sx: {
            p: 0,
            py: 0,
            width: '100%',
            '& .MuiCollapse-root': { width: '100%' },
            '& .MuiCollapse-wrapper': { width: '100%' },
            '& .MuiCollapse-wrapperInner': { width: '100%' },
          },
        }}
        enableExpandAll={false}
        enableTopToolbar={false}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableColumnActions={false}
        memoizeColumns={false}
        // Detail panels stay; expand UI is in the person cell — drop the MRT expand column.
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 0,
            minSize: 0,
            maxSize: 0,
            grow: false,
            enableResizing: false,
            Header: () => null,
            Cell: () => null,
            muiTableHeadCellProps: {
              sx: { display: 'none', p: 0, width: 0, minWidth: 0, maxWidth: 0 },
            },
            muiTableBodyCellProps: {
              sx: { display: 'none', p: 0, width: 0, minWidth: 0, maxWidth: 0 },
            },
          },
        }}
        state={{
          isLoading: loading,
          density: 'compact',
          columnVisibility: { 'mrt-row-expand': false },
        }}
      />
    </>
  );
}
